'use client';

import { useEffect, useId, type ReactNode } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';

import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { PasswordField } from '@eliadmin/core/src/components/nt/account/password/password-field';
import { PASSWORD_RULES_ISSUE } from '@eliadmin/core/src/components/nt/account/password/password-schema';
import {
  PasswordRules,
  PasswordStrength,
} from '@eliadmin/core/src/components/nt/account/password/password-strength';
import { useAccountTexts } from '@eliadmin/core/src/components/nt/shared/site-texts';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { IcoField } from './ico-field';
import { RegisterCheckbox, RegisterCountryField, RegisterTextField } from './register-fields';
import type { RegisterFormInput } from './register-schema';
import type { RegisterTexts } from './types';

/** Two fields side by side from sm (Figma rows 2 × 293 px, gap 16; stacked on the phone). */
const PAIR = 'grid items-start gap-3.5 sm:grid-cols-2 sm:gap-4';
const RULES_ID = 'register-password-rules';

/**
 * Figma „Card · …“ of the registration (221:13538): 1 px #E2E5DF border, radius 16, padding 24 and
 * 16 px rows (20 / 16 and 14 px on the phone); the title in Heading/H5, an optional Body/M #5D655D
 * line 4 px below it („Nepovinné údaje – …“).
 */
function RegisterCard({
  title,
  text,
  children,
}: {
  title: string;
  text?: string;
  children: ReactNode;
}) {
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className="flex min-w-0 flex-col gap-3.5 rounded-2xl border border-[#E2E5DF] bg-white px-4 py-5 sm:gap-4 sm:p-6"
    >
      <header className="flex flex-col gap-1">
        <h2 id={titleId} className="nt-h5 text-[#0D110D]">
          {title}
        </h2>
        {text ? <p className="nt-body-m text-[#5D655D]">{text}</p> : null}
      </header>
      {children}
    </section>
  );
}

/**
 * The address rows (Figma 221:13538 / 221:13989): „Ulica a číslo domu“ (one field, the update
 * splits it), „Mesto“ next to the 200 px „PSČ“ (112 px on the phone, 10 px apart), „Krajina“.
 * A person's billing address is optional – no asterisks but the country select.
 */
function AddressRows({
  delivery,
  required,
  countries,
  texts,
}: {
  delivery?: boolean;
  required: boolean;
  countries: Country[];
  texts: RegisterTexts;
}) {
  const { t } = useTranslations();
  const section = delivery ? 'shipping' : 'billing';

  return (
    <>
      <RegisterTextField
        name={delivery ? 'delivery_address' : 'address'}
        label={texts.street}
        placeholder={texts.street}
        required={required}
        autoComplete={`${section} street-address`}
      />
      <div className="grid grid-cols-[minmax(0,1fr)_112px] items-start gap-2.5 sm:grid-cols-[minmax(0,1fr)_200px] sm:gap-4">
        <RegisterTextField
          name={delivery ? 'delivery_city' : 'city'}
          label={t('text39')}
          placeholder={t('text39')}
          required={required}
          autoComplete={`${section} address-level2`}
        />
        <RegisterTextField
          name={delivery ? 'delivery_postcode' : 'postcode'}
          label={t('text40')}
          placeholder={texts.postcodePlaceholder}
          required={required}
          autoComplete={`${section} postal-code`}
        />
      </div>
      <RegisterCountryField
        name={delivery ? 'delivery_country' : 'country'}
        label={t('text519')}
        countries={countries}
        autoComplete={`${section} country`}
      />
    </>
  );
}

/** Meno | Priezvisko, then the phone with the courier hint (Figma „Osobné údaje“, „Kontaktná osoba“). */
function NamePhoneRows({ texts }: { texts: RegisterTexts }) {
  const { t } = useTranslations();

  return (
    <>
      <div className={PAIR}>
        <RegisterTextField
          name="name"
          label={t('text33')}
          placeholder={t('text33')}
          required
          autoComplete="given-name"
        />
        <RegisterTextField
          name="surname"
          label={t('text34')}
          placeholder={t('text34')}
          required
          autoComplete="family-name"
        />
      </div>
      <RegisterTextField
        name="phone"
        type="tel"
        inputMode="tel"
        label={t('text36')}
        required
        autoComplete="tel"
        hint={texts.phoneHint}
      />
    </>
  );
}

/**
 * Figma „Card · Prihlasovacie údaje“: the login e-mail with its helper, then „Nové heslo“ with the
 * strength meter and the admin rules of „Moje konto“ (12 px apart, 10 on the phone) next to
 * „Zopakujte heslo“, which turns green with „Heslá sa zhodujú.“ once both match. The designed rule
 * „Iné ako posledné 3 heslá“ is not shown – a new account has no previous passwords.
 */
export function CredentialsCard({ texts }: { texts: RegisterTexts }) {
  const { t } = useTranslations();
  const accountTexts = useAccountTexts();
  const { control, getFieldState, trigger } = useFormContext<RegisterFormInput>();
  const [password, confirmation] = useWatch({
    control,
    name: ['password', 'password_confirmation'],
  });
  const { errors } = useFormState({ control, name: ['password', 'password_confirmation'] });
  const matches = !!confirmation && confirmation === password && !errors.password_confirmation;

  // A changed password re-checks a repeat the customer already went through.
  useEffect(() => {
    if (getFieldState('password_confirmation').isTouched) void trigger('password_confirmation');
  }, [password, getFieldState, trigger]);

  return (
    <RegisterCard title={t('text385')}>
      <RegisterTextField
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="off"
        spellCheck={false}
        label={t('text35')}
        placeholder={texts.emailPlaceholder}
        required
        hint={texts.emailHint}
      />
      <div className={PAIR}>
        <div className="flex min-w-0 flex-col gap-2.5 sm:gap-3">
          <PasswordField
            name="password"
            label={t('text203')}
            autoComplete="new-password"
            describes={RULES_ID}
          />
          <PasswordStrength password={password} />
          <PasswordRules
            id={RULES_ID}
            password={password}
            showErrors={errors.password?.message === PASSWORD_RULES_ISSUE}
          />
        </div>
        <PasswordField
          name="password_confirmation"
          label={texts.repeatPassword}
          autoComplete="new-password"
          success={matches}
          hint={
            matches && accountTexts.passwordsMatch ? (
              <span className="text-[#0E6A20]">{accountTexts.passwordsMatch}</span>
            ) : undefined
          }
        />
      </div>
    </RegisterCard>
  );
}

/** Figma „Card · Osobné údaje“ (person): name, surname and the phone. */
export function PersonalCard({ texts }: { texts: RegisterTexts }) {
  const { t } = useTranslations();

  return (
    <RegisterCard title={t('text198')}>
      <NamePhoneRows texts={texts} />
    </RegisterCard>
  );
}

/**
 * Figma „Card · Fakturačná adresa“ (person): optional („Nepovinné údaje – …“), then „Doručovacia
 * adresa je iná ako fakturačná“, which adds the delivery card.
 */
export function BillingCard({ countries, texts }: { countries: Country[]; texts: RegisterTexts }) {
  const { t } = useTranslations();

  return (
    <RegisterCard title={t('text45')} text={texts.addressOptional}>
      <AddressRows required={false} countries={countries} texts={texts} />
      <RegisterCheckbox name="other_delivery" label={texts.otherAddress} />
    </RegisterCard>
  );
}

/**
 * Figma „Card · Údaje o firme“ (221:13989): IČO with the register lookup | „Názov firmy“, DIČ | IČ
 * DPH with its helper, the seat (required), „Tovar doručiť na inú adresu ako sídlo firmy“. The
 * designed „SK“ placeholder of IČ DPH has no text in the administration – not shown.
 */
export function CompanyCard({ countries, texts }: { countries: Country[]; texts: RegisterTexts }) {
  const { t } = useTranslations();

  return (
    <RegisterCard title={t('text689')}>
      <div className={PAIR}>
        <IcoField hint={texts.icoHint} />
        <RegisterTextField
          name="company"
          label={t('text328')}
          placeholder={t('text328')}
          required
          autoComplete="organization"
        />
      </div>
      <div className={PAIR}>
        <RegisterTextField name="dic" label={t('text43')} placeholder={t('text43')} required />
        <RegisterTextField name="ic_dph" label={t('text44')} hint={texts.icDphHint} />
      </div>
      <AddressRows required countries={countries} texts={texts} />
      <RegisterCheckbox name="other_delivery" label={texts.otherAddressCompany} />
    </RegisterCard>
  );
}

/**
 * Figma „Card · Doručovacia adresa“ (221:13989): a company names the site / recipient (required)
 * and the contact on site; a person (no frame in the design – the box is unticked there) gives the
 * address only. The rows are required once the customer asked for another address.
 */
export function DeliveryCard({
  company,
  countries,
  texts,
}: {
  company: boolean;
  countries: Country[];
  texts: RegisterTexts;
}) {
  const { t } = useTranslations();

  return (
    <RegisterCard title={t('text2139')}>
      {company ? (
        <div className={PAIR}>
          <RegisterTextField
            name="delivery_name"
            label={texts.deliveryName}
            placeholder={t('text3554')}
            required
            autoComplete="shipping organization"
          />
          <RegisterTextField
            name="delivery_contact"
            label={texts.deliveryContact}
            placeholder={t('text1250')}
            autoComplete="shipping name"
          />
        </div>
      ) : null}
      <AddressRows delivery required countries={countries} texts={texts} />
    </RegisterCard>
  );
}

/**
 * Figma „Card · Kontaktná osoba“ (company): the account holder's name and phone. The designed
 * separate e-mail is left out – the account has one e-mail, the login above.
 */
export function ContactPersonCard({ texts }: { texts: RegisterTexts }) {
  const { t } = useTranslations();

  return (
    <RegisterCard title={t('text1724')}>
      <NamePhoneRows texts={texts} />
    </RegisterCard>
  );
}
